import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import useSelectedDomain from '../useSelectedDomain';
import { getAdvancedSecurities } from '../../api/SecurityService';

export const useAdvancedSecurities = (initialFilters = {}) => {
  const [securities, setSecurities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 5,
    totalCount: 0,
    totalPages: 0,
    hasPreviousPage: false,
    hasNextPage: false
  });
  
  const [filters, setFilters] = useState({
    securityName: '',
    targetMarket: '',
    securityType: '',
    issuerName: '',
    holderType: 'All',
    sortBy: 'SecurityName',
    sortDescending: false,
    ...initialFilters
  });
  
  const selectedDomain = useSelectedDomain();
  const domainId = selectedDomain?.id;
  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isInitialLoadRef = useRef(true);

  const loadSecurities = useCallback(async (pageNumber = 1, searchFilters = filters, options = {}) => {
    if (!domainId) {
      setSecurities([]);
      setLoading(false);
      return;
    }

    const { silent = false } = options;

    // Annuler la requête précédente si elle existe
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Créer un nouveau AbortController
    abortControllerRef.current = new AbortController();
    
    // FIX PRINCIPAL : Ne pas mettre loading=true si c'est une recherche
    // Uniquement pour le chargement initial ou changement de page
    if (isInitialLoadRef.current && !silent) {
      setLoading(true);
    } else if (!silent) {
      // Pour les recherches, juste isSearching sans bloquer l'UI
      setIsSearching(true);
    }
    
    setError(null);
    
    try {
      const result = await getAdvancedSecurities(domainId, {
        ...searchFilters,
        pageNumber
      }, abortControllerRef.current.signal);
      
      setSecurities(result.items || []);
      setPagination({
        pageNumber: result.pageNumber || 1,
        pageSize: result.pageSize || 20,
        totalCount: result.totalCount || 0,
        totalPages: result.totalPages || 0,
        hasPreviousPage: result.hasPreviousPage || false,
        hasNextPage: result.hasNextPage || false
      });
      
      // Marquer que le chargement initial est terminé
      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        return;
      }
      
      setError(err.message || 'Nous sommes desole,une erreur s\'est produite lors du chargement des titres');
      
      if (err.response?.status !== 404 && !silent) {
        toast.error('Nous sommes désolés, une erreur s\'est produite lors du chargement des titres');
      }
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  }, [domainId, filters]);

  const handleFilterChange = useCallback((newFilters) => {
    // Mise à jour immédiate de l'état local (pour l'affichage)
    setFilters(prev => ({ ...prev, ...newFilters }));
    
    // Effacer le timeout précédent
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Indiquer qu'une recherche va avoir lieu (mais sans bloquer l'UI)
    setIsSearching(true);
    
    // Définir un nouveau timeout pour la recherche (500ms après la dernière frappe)
    searchTimeoutRef.current = setTimeout(() => {
      loadSecurities(1, { ...filters, ...newFilters }, { silent: false });
    }, 500);
  }, [filters, loadSecurities]);

  const handleSearch = useCallback(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    loadSecurities(1);
  }, [loadSecurities]);

  const handleResetFilters = useCallback(() => {
    const resetFilters = {
      securityName: '',
      targetMarket: '',
      securityType: '',
      issuerName: '',
      holderType: 'All',
      sortBy: 'SecurityName',
      sortDescending: false
    };
    
    setFilters(resetFilters);
    loadSecurities(1, resetFilters);
    toast.info('Filtres réinitialisés');
  }, [loadSecurities]);

  const handlePageChange = useCallback((pageNumber, newPageSize = pagination.pageSize) => {
    // Pour le changement de page, on peut mettre loading=true car c'est normal
    isInitialLoadRef.current = true;
    loadSecurities(pageNumber, { ...filters, pageSize: newPageSize });
  }, [filters, pagination.pageSize, loadSecurities]);

  // Chargement initial
  useEffect(() => {
    if (domainId) {
      loadSecurities();
    } else {
      setSecurities([]);
      setLoading(false);
    }
  }, [domainId]);

  // Nettoyage
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    securities,
    loading,
    isSearching,
    error,
    pagination,
    filters,
    setFilters: handleFilterChange,
    loadSecurities,
    handleSearch,
    handleResetFilters,
    handlePageChange
  };
};